<template>
	<div class="modal-overlay">
		<div class="modal-container">
			<div class="modal-header">
				<h3>Post-Disbursement Change</h3>
			</div>
			<Form
				:validation-schema="validationSchema"
				class="form-container"
				@submit="handleApply"
				v-slot="{ errors }"
			>
				<div v-for="field in fields" :key="field.name" :class="['form-input', field.width]">
					<label :for="field.name">
						{{ field.label }}
						<Popover v-if="field.tooltip" :text="field.tooltip" />
					</label>
					<Field
						v-if="field.type === 'select'"
						as="select"
						:id="field.name"
						:name="field.name"
						v-model="formData[field.name]"
					>
						<option></option>
						<option v-for="option in field.options" :key="option.id" :value="option.id">
							{{ option.name }}
						</option>
					</Field>
					<Field
						v-else
						:id="field.name"
						:name="field.name"
						:type="field.type"
						v-model="formData[field.name]"
					/>
					<ErrorMessage :name="field.name" class="error" />
				</div>

				<button class="submit-button col-6" type="submit" :disabled="!isFormValid(errors)">Apply</button>
				<button class="secondary col-3" type="button" @click.prevent="clearFormData">Clear</button>
				<button class="secondary col-3" type="button" @click.prevent="handleClose">Close</button>
			</Form>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { reactive, watchEffect, computed, defineExpose, onMounted } from 'vue';
	import { Form, Field, ErrorMessage } from 'vee-validate';
	import { Schema } from 'yup';
	import { ServiceType } from '@/models/loans';
	import Popover from '@/components/shared/Popover.vue';

	interface FormField {
		name: string;
		label: string;
		type: string;
		value: any;
		width: string;
		options?: ServiceType[];
		tooltip?: string;
	}

	const props = defineProps<{
		onApply: (formData: Record<string, any>) => void;
		onClose: () => void;
		fields: Array<FormField>;
		validationSchema: Schema;
	}>();

	const formData = reactive<Record<string, any>>({});
	const appliedFormData = reactive<Record<string, any>>({});

	const isFormValid = computed(() => {
		return (errors: Partial<Record<string, string | undefined>>) => errors && Object.keys(errors).length === 0;
	});

	function handleApply() {
		Object.assign(appliedFormData, formData); // persist applied changes
		props.onApply({ ...formData });
		props.onClose();
	}

	function clearFormData() {
		props.fields.forEach((field) => {
			formData[field.name] = field.value;
		});
	}

	function handleClose() {
		Object.assign(formData, appliedFormData); // reset to previously applied
		props.onClose();
	}

	defineExpose({
		clearFormData
	});

	watchEffect(() => {
		props.fields.forEach((field) => {
			formData[field.name] = field.value; // only update form
		});
	});

	onMounted(() => {
		props.fields.forEach((field) => {
			formData[field.name] = field.value;
		});

		Object.assign(appliedFormData, formData);
	});
</script>

<style scoped src="@/assets/css/loans/form.css" />

<style scoped>
	.modal-overlay {
		position: fixed;
		top: 0;
		left: 0;
		width: 100vw;
		height: 100vh;
		background-color: rgba(0, 0, 0, 0.5);
		display: flex;
		justify-content: center;
		align-items: center;
		z-index: 1000000; /* stupid navbar... */
	}

	.modal-header {
		margin-bottom: 1rem;
		display: flex;
		justify-content: space-between;
		align-items: baseline;
	}

	.modal-container {
		background: white;
		padding: 1rem;
		margin-right: 1rem;
		border-radius: 8px;
		width: 90%;
		max-width: 600px;
	}

	.modal-container button {
		padding: 10px;
		max-height: 40px;
	}
</style>
